import React, { useState, useEffect } from 'react';
import { prosthesisApi } from '../../services/api';
import { TypeProthese } from '../../types';
import TypeProtheseForm from './TypeProtheseForm';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Package,
  DollarSign,
  Clock,
  FileText
} from 'lucide-react';

interface TypeProtheseManagementProps {
  isVisible: boolean;
}

const TypeProtheseManagement: React.FC<TypeProtheseManagementProps> = ({ isVisible }) => {
  const [typesProtheses, setTypesProtheses] = useState<TypeProthese[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState<TypeProthese | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    fetchTypesProtheses();
  }, []);

  const fetchTypesProtheses = async () => {
    try {
      setLoading(true);
      const response = await prosthesisApi.getTypesProtheses();
      setTypesProtheses(response.data.results || []);
    } catch (error) {
      console.error('Erreur lors du chargement des types de prothèses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: Omit<TypeProthese, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      setFormLoading(true);
      if (editingType) {
        await prosthesisApi.updateTypeProthese(editingType.id, data);
      } else {
        await prosthesisApi.createTypeProthese(data);
      }
      await fetchTypesProtheses();
      setShowForm(false);
      setEditingType(null);
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (typeProthese: TypeProthese) => {
    setEditingType(typeProthese);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce type de prothèse ?')) {
      try {
        await prosthesisApi.deleteTypeProthese(id);
        await fetchTypesProtheses();
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingType(null);
  };

  const filteredTypes = typesProtheses.filter(type =>
    type.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    type.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'GNF',
      minimumFractionDigits: 0
    }).format(value);
  };

  if (showForm) {
    return (
      <TypeProtheseForm
        typeProthese={editingType}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        loading={formLoading}
      />
    );
  }

  if (!isVisible) return null;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Gestion des Types de Prothèses</h1>
        <button
          onClick={() => setShowForm(true)}
          className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Ajouter un type
        </button>
      </div>

      {/* Barre de recherche */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Rechercher par nom ou description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-gray-700 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          />
        </div>
      </div>

      {/* Liste des types de prothèses */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredTypes.length === 0 ? (
          <div className="text-center py-12">
            <Package className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun type de prothèse</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Aucun type ne correspond à votre recherche.' : 'Commencez par ajouter un type de prothèse.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type de prothèse
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Coût de base
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Délai
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTypes.map((type) => (
                  <tr key={type.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Package className="h-8 w-8 text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {type.nom}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-start gap-1">
                          <FileText className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-2">
                            {type.description.length > 100 
                              ? `${type.description.substring(0, 100)}...`
                              : type.description
                            }
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {formatCurrency(type.cout_base)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {type.delai_fabrication} jour{type.delai_fabrication > 1 ? 's' : ''}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(type)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(type.id)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <Package className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total types</p>
              <p className="text-lg font-semibold text-gray-900">{typesProtheses.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Prix moyen</p>
              <p className="text-lg font-semibold text-gray-900">
                {typesProtheses.length > 0 
                  ? formatCurrency(Math.round(typesProtheses.reduce((acc, t) => acc + t.cout_base, 0) / typesProtheses.length))
                  : formatCurrency(0)
                }
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-orange-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Délai moyen</p>
              <p className="text-lg font-semibold text-gray-900">
                {typesProtheses.length > 0 
                  ? Math.round(typesProtheses.reduce((acc, t) => acc + t.delai_fabrication, 0) / typesProtheses.length)
                  : 0
                } jours
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <Package className="h-8 w-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Type le plus cher</p>
              <p className="text-lg font-semibold text-gray-900">
                {typesProtheses.length > 0 
                  ? formatCurrency(Math.max(...typesProtheses.map(t => t.cout_base)))
                  : formatCurrency(0)
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Répartition par gamme de prix */}
      {typesProtheses.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Répartition par gamme de prix</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">
                {typesProtheses.filter(t => t.cout_base < 400000).length}
              </p>
              <p className="text-sm text-gray-600">Économique (&lt; 400,000 GNF)</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">
                {typesProtheses.filter(t => t.cout_base >= 400000 && t.cout_base < 700000).length}
              </p>
              <p className="text-sm text-gray-600">Standard (400,000 - 700,000 GNF)</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-600">
                {typesProtheses.filter(t => t.cout_base >= 700000).length}
              </p>
              <p className="text-sm text-gray-600">Premium (≥ 700,000 GNF)</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TypeProtheseManagement;